import { useState } from 'react'

import type { Citation, InsightBundle, MatrixResult } from '../lib/api'

type Tab = 'racional' | 'playbook' | 'ressalvas'

interface Props {
  bundle: InsightBundle | null
  result: MatrixResult
  onSelectTerritory: (territoryId: string) => void
  /** Ausente para quem não pode gastar cota (papel `viewer`): o botão some, e o
   * backend recusa com 403 de qualquer forma. */
  onGenerate?: () => Promise<void>
}

export function InsightsPanel({ bundle, result, onSelectTerritory, onGenerate }: Props) {
  const [tab, setTab] = useState<Tab>('racional')
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = () => {
    if (!onGenerate) return
    setGenerating(true)
    setError(null)
    onGenerate()
      .catch((exc: Error) => setError(exc.message))
      .finally(() => setGenerating(false))
  }
  const labels: Record<string, string> = Object.fromEntries(result.points.map((point) => [point.territory_id, point.label]))

  return (
    <section className="insights-section" aria-labelledby="insights-title">
      <header className="section-heading insights-heading">
        <div className="section-label"><span>03</span><p>Leitura estratégica</p></div>
        <div>
          <p className="eyebrow">Dos fatos para a ação</p>
          <h2 id="insights-title">O que o mapa muda na decisão?</h2>
        </div>
        <p className="section-deck">
          A interpretação usa somente fatos calculados e mantém suas referências expostas.
          Toda citação de território reabre o ponto correspondente no mapa.
        </p>
      </header>

      {!bundle ? (
        <div className="insights-empty">
          <span>—</span>
          <div>
            <p className="eyebrow">Leitura ainda não gerada</p>
            <h3>A matriz está pronta; o racional e o playbook ainda não.</h3>
            <p>
              Abrir esta página nunca dispara uma chamada de IA. A geração precisa ser
              uma decisão explícita porque usa cota e cria um novo artefato auditável.
            </p>
            {onGenerate ? (
              <button type="button" className="primary-button" onClick={generate} disabled={generating}>
                {generating ? 'Gerando leitura… (cerca de 1 minuto)' : 'Gerar leitura'}
              </button>
            ) : (
              <p className="role-note">Gerar a leitura é do papel analista neste projeto.</p>
            )}
            {error && <p className="brief-error" role="alert">{error}</p>}
          </div>
        </div>
      ) : (
        <div className="insights-workspace">
          <nav className="insight-tabs" aria-label="Seções da leitura">
            <TabButton active={tab === 'racional'} onClick={() => setTab('racional')} index="01"
              label="Racional" count={bundle.strategic_rationale.length} />
            <TabButton active={tab === 'playbook'} onClick={() => setTab('playbook')} index="02"
              label="Playbook" count={bundle.playbook.length} />
            <TabButton active={tab === 'ressalvas'} onClick={() => setTab('ressalvas')} index="03"
              label="Ressalvas" count={bundle.reading_caveats.length} />
          </nav>

          <div className="insight-content">
            {tab === 'racional' && (
              <ol className="editorial-list rationale-list">
                {bundle.strategic_rationale.map((claim, index) => (
                  <li key={claim.claim}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <article>
                      <p>{claim.claim}</p>
                      <Citations citations={claim.citations} labels={labels} onPick={onSelectTerritory} />
                    </article>
                  </li>
                ))}
              </ol>
            )}

            {tab === 'playbook' && (
              <ol className="editorial-list playbook-list">
                {bundle.playbook.map((action, index) => (
                  <li key={action.action}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <article>
                      <button type="button" className="territory-link" onClick={() => onSelectTerritory(action.territory_id)}>
                        {labels[action.territory_id] ?? action.territory_id}<span aria-hidden="true">↗</span>
                      </button>
                      <h3>{action.action}</h3>
                      <p>{action.rationale}</p>
                      <small>{action.kpi_link}{action.horizon ? ` · ${action.horizon}` : ''}</small>
                      <Citations citations={action.citations} labels={labels} onPick={onSelectTerritory} />
                    </article>
                  </li>
                ))}
              </ol>
            )}

            {tab === 'ressalvas' && (
              <ol className="editorial-list caveat-list">
                {bundle.reading_caveats.length === 0 && (
                  <li><span>00</span><article><p>Nenhuma ressalva: os dados exibidos sustentam esta leitura.</p></article></li>
                )}
                {bundle.reading_caveats.map((caveat, index) => (
                  <li key={caveat.caveat}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <article>
                      {caveat.territory_id && (
                        <button type="button" className="territory-link" onClick={() => onSelectTerritory(caveat.territory_id!)}>
                          {labels[caveat.territory_id] ?? caveat.territory_id}<span aria-hidden="true">↗</span>
                        </button>
                      )}
                      <p>{caveat.caveat}</p>
                      <small><strong>Impacto se ignorada:</strong> {caveat.impact}</small>
                      <Citations citations={caveat.citations} labels={labels} onPick={onSelectTerritory} />
                    </article>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <footer className="insight-provenance">
            <span>Modelo {bundle.model_version}</span>
            <span>Prompt {bundle.prompt_version}</span>
            <span>Fatos {bundle.facts_fingerprint.slice(0, 14)}…</span>
          </footer>
        </div>
      )}
    </section>
  )
}

function TabButton({ active, onClick, index, label, count }: {
  active: boolean
  onClick: () => void
  index: string
  label: string
  count: number
}) {
  return (
    <button type="button" className={active ? 'active' : undefined} onClick={onClick} aria-pressed={active}>
      <span>{index}</span><strong>{label}</strong><small>{count}</small>
    </button>
  )
}

export function Citations({ citations, labels, onPick }: {
  citations: Citation[]
  labels: Record<string, string>
  onPick: (territoryId: string) => void
}) {
  return (
    <ul className="citation-list" aria-label="Evidências citadas">
      {citations.map((citation) => {
        const isTerritory = citation.kind === 'territory' && citation.key in labels
        const text = `${labels[citation.key] ?? citation.key} · ${fieldLabel(citation.field)}`
        return (
          <li key={`${citation.kind}:${citation.key}:${citation.field}`}>
            {isTerritory ? (
              <button type="button" onClick={() => onPick(citation.key)}>{text}<span aria-hidden="true">↗</span></button>
            ) : <span>{citation.kind} · {text}</span>}
          </li>
        )
      })}
    </ul>
  )
}

function fieldLabel(value: string): string {
  const labels: Record<string, string> = {
    affinity_index: 'afinidade', competitive_saturation: 'afinidade competitiva', reach_pct: 'alcance',
    brand_affinity_index: 'ocupação', priority_score: 'prioridade', adjusted_priority_score: 'prioridade ajustada',
  }
  return labels[value] ?? value.replaceAll('_', ' ')
}
